import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Upload, Sparkles, Download, RefreshCw, Check, Image as ImageIcon, ArrowRight, Camera, ShieldAlert } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BACKGROUNDS = [
  { id: 'neutral-gray', label: 'Neutral gray (badge standard)' },
  { id: 'company-blue', label: 'Corporate navy blue' },
  { id: 'warm-cream', label: 'Warm cream' },
  { id: 'safety-white', label: 'ISO safety white' }
];
const UNIFORMS = [
  { id: 'preserve', label: 'Preserve what they\'re wearing' },
  { id: 'hi-vis', label: 'Hi-vis vest (orange)' },
  { id: 'polo', label: 'Company polo' },
  { id: 'coveralls', label: 'Coveralls' }
];

export default function Dashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const fileInput = useRef(null);

  const [preview, setPreview] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [background, setBackground] = useState('neutral-gray');
  const [uniform, setUniform] = useState('preserve');
  const [workerName, setWorkerName] = useState('');
  const [workerRole, setWorkerRole] = useState('');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [gallery, setGallery] = useState([]);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/headshots`, { params: { status: 'approved' } })
      .then((res) => setGallery(res.data.slice(0, 4).map((h) => ({
        id: h.id, name: h.worker_name, role: h.worker_role,
        image_url: `${process.env.REACT_APP_BACKEND_URL}${h.image_url}`,
      }))))
      .catch(() => {});
  }, [user]);

  if (authLoading) {
    return <div className="min-h-screen bg-[#0a0a0c] flex items-center justify-center"><RefreshCw className="w-6 h-6 text-[#ea9a6f] animate-spin" /></div>;
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0a0a0c]">
        <Navbar />
        <div className="max-w-md mx-auto text-center py-32 px-5">
          <h2 className="serif text-3xl text-white mb-3">Sign in to generate headshots</h2>
          <p className="text-[#8a877f] mb-6">Try CrewFace with 2 free headshots. No card required.</p>
          <Button onClick={() => navigate('/sign-up')} className="bg-[#ea9a6f] hover:bg-[#e0895a] text-[#0a0a0c] rounded-full px-6 h-11">Create free account</Button>
        </div>
        <Footer />
      </div>
    );
  }

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    setSelfieFile(f);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(f);
    setResult(null);
  };

  const generate = async () => {
    if (!selfieFile) {
      toast({ title: 'Upload a selfie first', description: 'Add a photo before generating.' });
      return;
    }
    setGenerating(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append('selfie', selfieFile);
      form.append('background', background);
      form.append('uniform', uniform);
      form.append('worker_name', workerName || 'New crew member');
      form.append('worker_role', workerRole || 'Field worker');
      const res = await axios.post(`${API}/headshots/generate`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      const data = res.data;
      setResult({
        id: data.id,
        img: `${process.env.REACT_APP_BACKEND_URL}${data.image_url}`,
        background, uniform,
        name: data.worker_name,
        role: data.worker_role,
      });
      toast({ title: 'Headshot ready', description: 'Approve or regenerate. Print-ready PDF available.' });
    } catch (err) {
      toast({ title: 'Generation failed', description: err.response?.data?.detail || 'Please try again.' });
    } finally {
      setGenerating(false);
    }
  };

  const saveToGallery = async () => {
    if (!result) return;
    try {
      await axios.patch(`${API}/headshots/${result.id}/status`, { status: 'approved' });
      setGallery([{ id: result.id, name: result.name, role: result.role, image_url: result.img }, ...gallery]);
      toast({ title: 'Saved to team gallery' });
    } catch {
      toast({ title: 'Could not save', description: 'Please try again.' });
    }
    setResult(null);
    setPreview(null);
    setSelfieFile(null);
    setFileName('');
    setWorkerName('');
    setWorkerRole('');
  };

  const exportBadge = () => {
    if (!result) return;
    window.open(`${API}/headshots/${result.id}/badge-pdf`, '_blank');
    toast({ title: 'Download started', description: 'ID badge PDF exporting.' });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0c]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 md:px-8 pt-8 md:pt-12 pb-16">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
          <div>
            <div className="mono text-xs text-[#7a7770] uppercase tracking-wider mb-1">Generator</div>
            <h1 className="serif text-4xl md:text-5xl text-white">Generate a headshot</h1>
            <p className="text-[#8a877f] mt-2">Signed in as <span className="text-white">{user.email}</span> · {user.company}</p>
          </div>
          <Link to="/team"><Button variant="outline" className="border-[#2a2a30] bg-transparent text-white hover:bg-[#14141a] rounded-full">Team dashboard <ArrowRight className="ml-1.5 w-4 h-4" /></Button></Link>
        </div>

        <div className="grid lg:grid-cols-3 gap-4">
          {/* Upload column */}
          <div className="lg:col-span-1 bg-[#101014] border border-[#1e1e26] rounded-2xl p-6">
            <div className="text-xs uppercase tracking-wider text-[#7a7770] mb-3">1. Selfie</div>
            <div
              onClick={() => fileInput.current?.click()}
              data-testid="selfie-upload-zone"
              className="aspect-[3/4] rounded-xl border-2 border-dashed border-[#2a2a30] bg-[#0a0a0c] flex flex-col items-center justify-center cursor-pointer hover:border-[#ea9a6f] transition-colors overflow-hidden relative"
            >
              {preview ? (
                <img src={preview} alt="selfie preview" className="w-full h-full object-cover" />
              ) : (
                <div className="text-center px-5">
                  <Camera className="w-8 h-8 text-[#5a5850] mx-auto mb-3" />
                  <div className="text-white text-sm font-medium">Upload a selfie</div>
                  <div className="text-xs text-[#7a7770] mt-1">JPG or PNG. Phone camera is fine.</div>
                </div>
              )}
              <input ref={fileInput} onChange={onFile} type="file" accept="image/*" className="hidden" data-testid="selfie-file-input" />
            </div>
            {fileName && <div className="mono text-xs text-[#7a7770] mt-3 truncate">{fileName}</div>}

            <div className="mt-5 space-y-3">
              <div>
                <label className="text-xs text-[#c9c6bf]">Worker name</label>
                <input value={workerName} onChange={(e) => setWorkerName(e.target.value)} placeholder="e.g. Marcus J." data-testid="worker-name-input" className="mt-1 w-full bg-[#0a0a0c] border border-[#26262e] rounded-md h-10 px-3 text-white text-sm outline-none focus:border-[#ea9a6f]" />
              </div>
              <div>
                <label className="text-xs text-[#c9c6bf]">Role</label>
                <input value={workerRole} onChange={(e) => setWorkerRole(e.target.value)} placeholder="e.g. Site Foreman" data-testid="worker-role-input" className="mt-1 w-full bg-[#0a0a0c] border border-[#26262e] rounded-md h-10 px-3 text-white text-sm outline-none focus:border-[#ea9a6f]" />
              </div>
            </div>
          </div>

          {/* Options + generate */}
          <div className="lg:col-span-1 bg-[#101014] border border-[#1e1e26] rounded-2xl p-6">
            <div className="text-xs uppercase tracking-wider text-[#7a7770] mb-3">2. Brand pack</div>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-[#c9c6bf]">Background</label>
                <Select value={background} onValueChange={setBackground}>
                  <SelectTrigger className="mt-1 bg-[#0a0a0c] border-[#26262e] text-white h-10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#101014] border-[#26262e] text-white">
                    {BACKGROUNDS.map((b) => <SelectItem key={b.id} value={b.id} className="focus:bg-[#1a1a20] focus:text-white">{b.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-[#c9c6bf]">Uniform</label>
                <Select value={uniform} onValueChange={setUniform}>
                  <SelectTrigger className="mt-1 bg-[#0a0a0c] border-[#26262e] text-white h-10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#101014] border-[#26262e] text-white">
                    {UNIFORMS.map((u) => <SelectItem key={u.id} value={u.id} className="focus:bg-[#1a1a20] focus:text-white">{u.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="bg-[#0a0a0c] border border-[#1e1e26] rounded-lg p-3">
                <div className="text-xs text-[#7a7770] uppercase tracking-wider mb-2">Export</div>
                <div className="flex flex-wrap gap-1.5">
                  {['2x2 in', '35x45mm', 'DOT card', 'PNG 1024', 'PDF badge'].map((e) => (
                    <Badge key={e} className="bg-[#14141a] border border-[#26262e] text-[#c9c6bf] hover:bg-[#14141a] font-normal">{e}</Badge>
                  ))}
                </div>
              </div>
            </div>

            <Button onClick={generate} disabled={generating} data-testid="generate-headshot-button" className="w-full mt-5 bg-[#ea9a6f] hover:bg-[#e0895a] text-[#0a0a0c] font-medium rounded-full h-11 glow-coral">
              {generating ? (<><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generating…</>) : (<><Sparkles className="w-4 h-4 mr-2" /> Generate headshot</>)}
            </Button>
            <p className="mono text-[10px] text-[#5a5850] text-center mt-3">Real AI generation • ~30-60s</p>
          </div>

          {/* Result */}
          <div className="lg:col-span-1 bg-[#101014] border border-[#1e1e26] rounded-2xl p-6">
            <div className="text-xs uppercase tracking-wider text-[#7a7770] mb-3">3. Result</div>
            <div className="aspect-[3/4] rounded-xl bg-[#0a0a0c] border border-[#1e1e26] flex items-center justify-center overflow-hidden relative">
              {generating && (
                <div className="text-center" data-testid="generating-indicator">
                  <div className="relative w-14 h-14 mx-auto mb-4">
                    <div className="absolute inset-0 rounded-full border-2 border-[#26262e]" />
                    <div className="absolute inset-0 rounded-full border-2 border-t-[#ea9a6f] animate-spin" />
                  </div>
                  <div className="text-white text-sm">Generating with GPT-image-1…</div>
                  <div className="text-xs text-[#7a7770] mt-1">Preserving PPE, matching brand pack</div>
                </div>
              )}
              {result && !generating && (
                <>
                  <img src={result.img} alt="result" data-testid="generated-headshot-image" className="w-full h-full object-cover" />
                  <div className="absolute top-3 left-3 bg-[#7dd4c5] text-[#0a0a0c] text-[10px] font-semibold px-2 py-1 rounded-full flex items-center gap-1">
                    <Check className="w-3 h-3" /> Badge-ready
                  </div>
                </>
              )}
              {!generating && !result && (
                <div className="text-center px-5">
                  <ImageIcon className="w-8 h-8 text-[#3a3a44] mx-auto mb-3" />
                  <div className="text-sm text-[#7a7770]">Your headshot will appear here.</div>
                </div>
              )}
            </div>

            {result && !generating && (
              <div className="mt-4 space-y-2">
                <Button onClick={saveToGallery} data-testid="approve-save-button" className="w-full bg-[#7dd4c5] hover:bg-[#6bc4b5] text-[#0a0a0c] font-medium rounded-full h-10">
                  <Check className="w-4 h-4 mr-2" /> Approve & save to team
                </Button>
                <div className="grid grid-cols-2 gap-2">
                  <Button onClick={generate} variant="outline" data-testid="regenerate-button" className="border-[#2a2a30] bg-transparent text-white hover:bg-[#14141a] rounded-full h-10">
                    <RefreshCw className="w-4 h-4 mr-1.5" /> Regenerate
                  </Button>
                  <Button onClick={exportBadge} variant="outline" data-testid="export-badge-button" className="border-[#2a2a30] bg-transparent text-white hover:bg-[#14141a] rounded-full h-10">
                    <Download className="w-4 h-4 mr-1.5" /> Export
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Team gallery */}
        <div className="mt-12">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="mono text-xs text-[#7a7770] uppercase tracking-wider mb-1">Recent</div>
              <h2 className="serif text-3xl text-white">Team gallery</h2>
            </div>
            <Link to="/team" className="text-sm text-[#ea9a6f] hover:underline">View all →</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {gallery.map((h) => (
              <div key={h.id} data-testid="gallery-item" className="rounded-2xl overflow-hidden border border-[#1e1e26] bg-[#101014] hover:border-[#2e2e38] transition-colors">
                <img src={h.image_url} alt={h.name} className="w-full aspect-[3/4] object-cover" />
                <div className="p-3">
                  <div className="text-white text-sm font-medium">{h.name}</div>
                  <div className="text-xs text-[#7a7770]">{h.role}</div>
                </div>
              </div>
            ))}
            {gallery.length === 0 && (
              <div className="col-span-2 md:col-span-4 text-center text-sm text-[#7a7770] py-8">No approved headshots yet. Generate one above.</div>
            )}
          </div>
        </div>

        <div className="mt-10 flex items-start gap-2 text-xs text-[#7a7770] bg-[#101014] border border-[#1e1e26] rounded-xl p-4">
          <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
          <span>CrewFace output is for identification and internal use. It is not a substitute for legal, HR compliance, or DOT-required medical certification advice.</span>
        </div>
      </div>
      <Footer />
    </div>
  );
}
